import "./recovery-refund.css";
import { useEffect, useRef, useState } from "react";
import { useAppState } from "../../app-state";
import { quoteRecoveryRefund, submitRecoveryRefund } from "../../lib/recovery-api";
import { REFUND_MAX_RATE, REFUND_MIN_RATE, buildRefund, reconstructRefundSource, validateSignedRefund, type RefundDestination, type RefundQuote } from "../../lib/recovery-refund";
import { saveRecoveryRefundAttempt, type RecoveryRecord } from "../../lib/recovery";
import { assertRecoveryRefundWallet, assertWalletSelected, signRecoveryRefund } from "../../lib/wallets";
import { transactionExplorerUrl } from "../../lib/explorer-links";

export function RecoveryRefund({ record, onSigned }: { record: RecoveryRecord; onSigned?: (attempt: NonNullable<RecoveryRecord["refundAttempt"]>) => void }) {
  const { wallet, authorizeWallet } = useAppState();
  const [open, setOpen] = useState(false);
  const [destination, setDestination] = useState<RefundDestination>("payment");
  const [rate, setRate] = useState(1);
  const [quote, setQuote] = useState<RefundQuote | null>(null);
  const [preview, setPreview] = useState<Omit<RefundQuote, "authorization" | "expiresAt" | "psbt"> | null>(null);
  const [attempt, setAttempt] = useState(record.refundAttempt);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [txid, setTxid] = useState("");
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  if (!wallet) return null;
  const authenticated = async () => {
    await assertWalletSelected(wallet);
    const auth = await authorizeWallet(true);
    if (!mounted.current) throw new Error("Wallet changed during recovery; no transaction was sent");
    if (!auth) throw new Error("Connect and verify both wallet addresses first");
    return auth;
  };
  const review = () => {
    setError(""); setQuote(null); setPreview(null);
    try {
      const pair = { paymentAddress: wallet.payment.address, ordinalsAddress: wallet.ordinals.address, ordinalsPublicKey: wallet.ordinals.publicKey };
      const source = reconstructRefundSource(record.package, pair);
      const plan = buildRefund(source, pair, destination, rate);
      setPreview({ source, destination, address: plan.address, feeRate: rate, vsize: plan.vsize, minerFee: plan.minerFee, refundAmount: plan.refundAmount });
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Refund review unavailable"); }
  };
  const verify = async () => {
    setBusy(true); setError(""); setQuote(null);
    try {
      await assertRecoveryRefundWallet(wallet);
      const auth = await authenticated();
      setQuote(await quoteRecoveryRefund(record.package, auth, destination, rate));
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Refund review unavailable"); }
    finally { setBusy(false); }
  };
  const submit = async () => {
    if (!quote && !attempt) return;
    setBusy(true); setError("");
    try {
      const auth = await authenticated();
      let approved = attempt?.quote ?? quote!;
      if (approved.expiresAt <= Date.now()) {
        if (!attempt) throw new Error("This fee review expired. Review the refund again before signing.");
        approved = await quoteRecoveryRefund(record.package, auth, approved.destination, approved.feeRate);
      }
      const raw = attempt?.raw ?? await signRecoveryRefund(wallet, approved);
      validateSignedRefund(raw, approved.source, { paymentAddress: wallet.payment.address, ordinalsAddress: wallet.ordinals.address, ordinalsPublicKey: wallet.ordinals.publicKey }, approved.destination, approved.feeRate);
      await saveRecoveryRefundAttempt(record.id, { quote: approved, raw });
      setAttempt({ quote: approved, raw });
      onSigned?.({ quote: approved, raw });
      await assertWalletSelected(wallet);
      if (!mounted.current) throw new Error("Wallet changed during recovery; the signed refund was preserved but not sent");
      const sent = await submitRecoveryRefund(record.package, auth, approved, raw);
      setTxid(sent.txid);
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Refund unavailable; preserve your saved package"); }
    finally { setBusy(false); }
  };
  const shown = attempt?.quote ?? quote ?? preview;
  return <div className="recovery-refund">
    <button type="button" disabled={busy} onClick={() => setOpen(!open)}>REFUND TO WALLET</button>
    {open ? <div>
      <p><strong>Refund cancels this gallery attempt. It does not move or inscribe the separately selected sat.</strong></p>
      <p>Only the saved commit output is returned, minus the Bitcoin miner fee. STC recovery fee: 0 sats. Your saved package and site data are preserved.</p>
      {!attempt && !txid ? <>
        <label>DESTINATION <select value={destination} disabled={busy} onChange={e => { setDestination(e.target.value as RefundDestination); setQuote(null); setPreview(null); }}>
          <option value="payment">Original payment wallet (recommended)</option>
          <option value="ordinals">Connected Taproot / ordinals wallet</option>
        </select></label>
        <label>FEE RATE (sat/vB) <input type="number" min={REFUND_MIN_RATE} max={REFUND_MAX_RATE} step="0.1" value={rate} disabled={busy} onChange={e => { setRate(Number(e.target.value)); setQuote(null); setPreview(null); }} /></label>
        <button type="button" disabled={busy} onClick={review}>REVIEW REFUND</button>
      </> : null}
      {shown ? <>
        <dl>
          <dt>SOURCE OUTPOINT</dt><dd>{shown.source.txid}:{shown.source.index}</dd>
          <dt>COMMIT VALUE</dt><dd>{shown.source.value.toLocaleString()} sats</dd>
          <dt>DESTINATION</dt><dd>{shown.address}</dd>
          <dt>FEE RATE / SIZE</dt><dd>{shown.feeRate} sat/vB · {shown.vsize} vB</dd>
          <dt>MINER FEE</dt><dd>{shown.minerFee.toLocaleString()} sats (checked exactly after signing)</dd>
          <dt>YOU RECEIVE</dt><dd>{shown.refundAmount.toLocaleString()} sats</dd>
        </dl>
        {!quote && !attempt ? <>
          <p>Verify your wallet to check this output is still available. Wallet ownership verification is free and does not send Bitcoin.</p>
          <button type="button" disabled={busy} onClick={() => void verify()}>{busy ? "CHECKING…" : "VERIFY WALLET & REFUND"}</button>
        </> : !txid ? <button type="button" disabled={busy} onClick={() => void submit()}>{busy ? "PROCESSING…" : attempt ? "RETRY SAME SIGNED REFUND" : "SIGN & SEND REFUND"}</button> : null}
      </> : null}
      {error ? <p role="alert">{error}</p> : null}
      {txid ? <p role="status">REFUND SENT · <a href={transactionExplorerUrl(txid)} target="_blank" rel="noreferrer">VIEW TRANSACTION ↗</a></p> : null}
    </div> : null}
  </div>;
}
